/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import classnames from 'classnames';
import _ from 'lodash';

import './BottomDrawer.scss';
import { isMobileApplication } from '../../../../utilities/MobileUtil';

const BottomDrawerContext = React.createContext([]);

export const BottomDrawerProvider = (props) => {
  const [state, setState] = React.useState({ scrollPos: 0 });

  return (
    <BottomDrawerContext.Provider value={[state, { setScrollPos: (v) => { setState({ scrollPos: v }); } }]}>
      {props.children}
    </BottomDrawerContext.Provider>
  );
};

export const useBottomDrawer = () => {
  return React.useContext(BottomDrawerContext);
};

const NUMBER_REGEX = /[-]{0,1}[\d]*[.]{0,1}[\d]+/g;

function easeInOutCubic(timeElapsed, duration, totalDelta, initialValue) {
  const x = timeElapsed / duration;
  const val = x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;

  return {
    value: (totalDelta * val) + initialValue,
    percentProgress: x,
  };
}

function getStylesForExpandedState(expandedState, offsetHeight) {
  switch (expandedState) {
    case 'menu':
      return {
        top: window.innerHeight - offsetHeight,
        paddingBottom: window.innerHeight,
      };
    case 'partial':
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
      };
    case 'fullscreen':
      return {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
      };
    case '90-fullscreen':
    case 'hidden':
    default:
      return {};
  }
}
function getDestinationY(expandedState, marginTop, menuOffsetHeight) {
  switch (expandedState) {
    case 'menu':
      const mt = marginTop || 0;
      return window.innerHeight - mt - menuOffsetHeight;
    case 'partial':
      return window.innerHeight / 2;
    case '90-fullscreen':
      return 10;
    case 'fullscreen':
    case 'hidden':
    default:
      return 0;
  }
}
interface Props {
  expandedState: 'hidden' | 'menu' | 'partial' | '90-fullscreen' | 'fullscreen';
  className?: string;
  hasPadding?: boolean;
  hasRoundedCorners?: boolean;
  style?: any;
}
const BottomDrawer: React.FC<Props> = (props) => {
  const [, actions] = useBottomDrawer();
  const [menuOffsetHeight, setMenuOffsetHeight] = React.useState(null);
  const [styleState, setStyleState] = React.useState<any>(() => ({
    ...getStylesForExpandedState(props.expandedState, menuOffsetHeight),
    top: getDestinationY(props.expandedState, 0, 0),
  }));

  const classStyle = classnames('cmp-bottomdrawer', props.className, {
    'has-padding': props.hasPadding,
    'has-roundedcorners': props.hasRoundedCorners,
  });

  React.useEffect(() => {
    const handleResize = () => {
      if (props.expandedState !== 'menu') { return; }

      const newHeight = containerRef.current.firstElementChild.offsetHeight;
      setMenuOffsetHeight(newHeight);
      setStyleState({
        ...getStylesForExpandedState(props.expandedState, menuOffsetHeight),
        top: getDestinationY(props.expandedState, styleState.marginTop, newHeight),
      });
    };
    const throttledResize = _.throttle(handleResize, 100);
    window.addEventListener('resize', throttledResize);

    return () => {
      window.removeEventListener('resize', throttledResize);
    };
  }, [props.expandedState]);

  React.useEffect(() => {
    const setScroll = () => {
      actions.setScrollPos(window.scrollY);
    };

    window.addEventListener('scroll', setScroll);

    const initialHeight = containerRef.current.firstElementChild.offsetHeight;
    setMenuOffsetHeight(initialHeight);
    setStyleState({
      ...getStylesForExpandedState(props.expandedState, initialHeight),
      top: getDestinationY(props.expandedState, 0, initialHeight),
    });

    return () => {
      window.removeEventListener('scroll', setScroll);
    };
  }, []);

  const containerRef = React.useRef(null);
  const animationRef = React.useRef(null);
  React.useEffect(() => {
    if (!Boolean(containerRef.current)) {
      return;
    }
    const clearAnimationInterval = () => {
      if (animationRef.current) {
        const el = document.getElementsByTagName('body')[0];
        el.classList.remove('__hide-scroll--cmp-bottomdrawer');
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    };

    clearAnimationInterval();

    let finalCallback;
    const topVal = containerRef.current.style.top.match(NUMBER_REGEX) || [''];
    const initialPos = Number(topVal);

    switch (props.expandedState) {
      case 'menu':
        const newHeight = containerRef.current.firstElementChild.offsetHeight;
        if (newHeight !== menuOffsetHeight) {
          setMenuOffsetHeight(newHeight);
          return;
        }

        setStyleState({
          ...styleState,
          paddingBottom: window.innerHeight,
        });
        if (window.scrollY > 0) {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          }, 90);
        }
        finalCallback = () => {
          setStyleState(getStylesForExpandedState(props.expandedState, menuOffsetHeight));
        };
        break;
      case 'fullscreen':
        finalCallback = () => {
          setStyleState(getStylesForExpandedState(props.expandedState, menuOffsetHeight));
        };
        break;
      case 'partial':
        // this never has an animation
        finalCallback = () => {};
        break;
      case '90-fullscreen':
      case 'hidden':
      default:
        finalCallback = () => {};
        break;
    }
    const marginTop = styleState.marginTop || 0;
    const destY = getDestinationY(props.expandedState,  marginTop, menuOffsetHeight);
    const duration = 600;
    const deltaPos = destY - initialPos;
    const startTime = +new Date();

    const animationStep = () => {
      const currentTime = +new Date();
      const { value, percentProgress } = easeInOutCubic(currentTime - startTime, duration, deltaPos, initialPos);
      if (percentProgress < 0.96) {
        containerRef.current.style.top = `${value}px`;
      } else {
        containerRef.current.style.top = `${destY}px`;
        clearAnimationInterval();
        finalCallback();
      }
    };

    const el = document.getElementsByTagName('body')[0];
    el.classList.add('__hide-scroll--cmp-bottomdrawer');
    animationRef.current = setInterval(animationStep, 20);

    return clearAnimationInterval;
  }, [props.expandedState, menuOffsetHeight]);

  React.useEffect(() => {
    const onShowKeyboard = (e) => {
      if (!e) { return; }

      const scrollToY = e.keyboardHeight ? (e.keyboardHeight - 90) : 0;
      setTimeout(() => {
        window.scroll(0, scrollToY);
      }, 300);
    };

    const onHideKeyboard = (e) => {
      setTimeout(() => {
        // add one so it will trigger rerender/animation
        const newHeight = containerRef.current.firstElementChild.offsetHeight + 1;
        setMenuOffsetHeight(newHeight);
      }, 300);
    };

    if (isMobileApplication()) {
      window.addEventListener('native.keyboardshow', onShowKeyboard);
      window.addEventListener('native.keyboardhide', onHideKeyboard);
    }
    return () => {
      window.removeEventListener('native.keyboardshow', onShowKeyboard);
      window.removeEventListener('native.keyboardhide', onHideKeyboard);
    };
  }, [props.expandedState]);

  return (
    <div className={classStyle} ref={containerRef} style={{ ...styleState, ...props.style }}>
      {props.children}
    </div>
  );
};

export default BottomDrawer;
